// src/encounter/services/use-radiology-search.ts
// Radiology (imaging) catalog search for the A/P imaging-add picker. Two sources:
//   • PROVIDER catalog — GET /api/Provider/GetRadiololgy?ProviderId=&searchQuery=
//     (NOTE the LIVE-ENDPOINT TYPO "Radiololgy" — kept exactly; the .NET route is misspelled).
//   • MASTER fallback — GET /api/Master/RadiologyCodesList?searchQuery= (full catalog, no provider)
// The provider catalog is the practitioner's favourited/scoped studies; when there's no providerId
// we fall back to the full master list. Both endpoints ship rows as {Id, code:{code, display}}
// (live-probed 2026-06-10) — the wrapped-Coding shape procedureOptionsToCodeSearchResults already
// unwraps via normalizeProcedureCode (lifts inner Coding, reads displayName ?? display). So both
// responses map to the DS CodeSearchResult[] the CodeSearchField renders, with no per-source branch
// in the mapper. Min 2 chars to avoid empty-search spam (the catalog is large); consumer debounces.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { procedureOptionsToCodeSearchResults } from '../lib/to-code-search-result';
import type { CodeSearchResult } from '@scripsteam/scrips-signal-ds';
import type { CodeDisplayName } from '@/types/patient-chart-api';

const MIN_QUERY_LENGTH = 2;

export function useRadiologySearch(providerId: string | null | undefined, query: string) {
  const q = query.trim();
  return useQuery<CodeSearchResult[]>({
    queryKey: ['radiology-search', providerId ?? null, q],
    enabled: q.length >= MIN_QUERY_LENGTH,
    queryFn: async ({ signal }) => {
      const url = providerId
        ? `/api/Provider/GetRadiololgy?ProviderId=${encodeURIComponent(providerId)}&searchQuery=${encodeURIComponent(q)}`
        : `/api/Master/RadiologyCodesList?searchQuery=${encodeURIComponent(q)}`;
      // Rows are {Id, code:{code, display}} (wrapped Coding); the mapper unwraps each.
      const res = await get<CodeDisplayName[]>(url, { signal });
      return procedureOptionsToCodeSearchResults(res ?? []);
    },
  });
}
